import { AxiosResponse } from 'axios';

import { challengeServiceClient } from '@/services/api';
import { Challenge } from '@/types/Challenge';
import { Judge, JudgeSchema } from '@/types/Judge';
import { Response } from '@/types/Response';
import { UserProfile } from '@/types/UserProfile';

class ResponseService {
    static async getUserResponseCount(user_id: string): Promise<number> {
        const resp = await challengeServiceClient.get('/challenge/user-responses-count', {
            params: {
                user_id,
            },
        });
        return resp?.data?.count || 0;
    }

    static async getResponseView(
        access_token: string,
        response_id: string,
        challenge_id: string
    ): Promise<{
        message: string;
        response: Response;
        challenge: Challenge;
        judge: Judge;
    }> {
        const response = await challengeServiceClient.get(`/challenge/get-response-view`, {
            params: {
                response_id,
                challenge_id,
            },
            headers: {
                Authorization: `Bearer ${access_token}`,
                'Content-Type': 'application/json',
            },
        });
        console.log('getResponseView', response.data);
        return {
            message: response.data.message,
            response: response.data.response,
            challenge: response.data.challenge,
            judge: response.data.judge,
        };
    }

    static async judgeResponse(
        accessToken: string,
        response_id: string,
        challenge_id: string,
        points: number
    ): Promise<{
        message: string;
        judge: Judge;
    }> {
        const response = await challengeServiceClient.post(
            `/challenge/judge_response`,
            {
                response_id,
                challenge_id,
                points,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
        console.log('getResponseView', response.data);
        return {
            message: response.data.message,
            judge: response.data.judge,
        };
    }

    /**
     * Fetches a response by its ID.
     * @param response_id - The ID of the response to retrieve.
     * @returns A Promise that resolves to a Response object or null if not found.
     */
    static async getResponseById(response_id: string): Promise<Response | null> {
        const resp: AxiosResponse<{ response: Response }> = await challengeServiceClient.get(
            `/challenge/get-response`,
            {
                params: {
                    response_id,
                },
            }
        );
        return resp?.data?.response || null;
    }

    static async updateResponse(fullResponse: Record<string, any>) {
        console.log('updateResponse response:', fullResponse);
        const resp: AxiosResponse<{ response: Response; message: string }> =
            await challengeServiceClient.post('/challenge/update-response', fullResponse);
        return {
            response: resp?.data?.response || null,
            message: resp?.data?.message || null,
        };
    }

    static async addView(response_id: string) {
        try {
            const resp = await challengeServiceClient.post('/challenge/add-view', {
                parent_id: response_id,
                parent_type: 'RESPONSE',
            });
            if (!resp) {
                console.error('Failed to add view to response');
                return false;
            }

            return resp.data.response;
        } catch (error) {
            console.error('Error adding view:', error);
            return false;
        }
    }

    static async publishResponse(
        accessToken: string,
        response: Response
    ): Promise<Response | null> {
        const resp = await challengeServiceClient.post(
            '/challenge/update-response',
            {
                response_id: response.PK,
                challenge_id: response.challenge_id,
                status: 'published',
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );

        // If the response is successful and contains a valid Response object
        if (resp.status === 200 && resp.data.response) {
            const updatedResponse: Response = {
                ...resp.data.response, // Ensure it matches the interface shape
            };
            return updatedResponse;
        }

        console.error('Failed to publish response:', resp.data.message || 'Unknown error');
        return null;
    }

    static async getReponsesForChallenge(
        challenge_id: string,
        limit = 10,
        last_evaluated_key: any = null,
        accessToken: string | null = null
    ): Promise<{
        responses: Response[];
        challenges: Challenge[];
        judge: Judge | null;
        lastEvaluatedKey: any;
        user_likes: string[];
        user_profiles: UserProfile[]; // When you have a UserProfile interface update this.
    }> {
        const serializedKey = last_evaluated_key ? JSON.stringify(last_evaluated_key) : null;

        const resp = await challengeServiceClient.get('/challenge/get-responses-for-challenge', {
            params: {
                challenge_id,
                limit,
                last_evaluated_key: serializedKey,
            },
            headers: {
                Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
            },
        });

        const validatedJudge = resp.data.judge ? JudgeSchema.parse(resp.data.judge) : null;

        return {
            responses: resp.data.responses || [],
            challenges: resp.data.challenges || [],
            judge: validatedJudge,
            lastEvaluatedKey: resp.data.last_evaluated_key || null,
            user_likes: resp.data.user_likes || [],
            user_profiles: resp.data.user_profiles || [],
        };
    }

    static async getFinalReponsesForChallenge(
        challenge_id: string,
        limit = 1000,
        last_evaluated_key: any = null
    ): Promise<{
        responses: Response[];
        lastEvaluatedKey: any;
        user_likes: string[];
        user_profiles: UserProfile[]; // When you have a UserProfile interface update this.
    }> {
        const serializedKey = last_evaluated_key ? JSON.stringify(last_evaluated_key) : null;

        const resp = await challengeServiceClient.get('/challenge/challenge-final-responses', {
            params: {
                challenge_id,
                limit,
                last_evaluated_key: serializedKey,
            },
        });

        return {
            responses: resp.data.responses || [],
            lastEvaluatedKey: resp.data.last_evaluated_key || null,
            user_likes: resp.data.user_likes || [],
            user_profiles: resp.data.user_profiles || [],
        };
    }
}

export default ResponseService;
